import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'src/app/components/Button/Button';
import { GA_EVENT_CATEGORY } from 'src/app/constants/analytics';
import { pushSubscribeAnalytics, EventTrack } from 'src/app/helpers/analytics';
import { getSubscribeMutationOptions } from 'src/app/helpers/graphql';
import { LoginFunction } from 'src/app/models/login';
import { ProgramWithSubscribe } from 'src/app/models/program';
import { USER_ID_QUERY, UserIdOnly } from 'src/app/models/user';

interface MyProps extends ProgramWithSubscribe {
  openLoginPopup: LoginFunction;
  showToast: FunctionStringCallback;
  classname?: string;
  channelName?: string;
}

interface MyQueryResult {
  user?: UserIdOnly;
}

const GRAPHQL_SUBSCRIBE_MUTATION = gql`
  mutation subscribeProgram($programId: ID!) {
    subscribe(programId: $programId) {
      isSubscribed
    }
  }
`;

const GRAPHQL_UNSUBSCRIBE_MUTATION = gql`
  mutation unsubscribeProgram($programId: ID!) {
    unsubscribe(programId: $programId) {
      isSubscribed
    }
  }
`;

const GRAPHQL_USER_ID_QUERY = gql`
  query user {
    user {
      ${USER_ID_QUERY}
    }
  }
`;

export const ProgramSubscribeButton: FunctionComponent<MyProps> = (props: MyProps): JSX.Element | null => {
  const { openLoginPopup, showToast, name, classname, channelName, isSubscribed } = props;
  const { data } = useQuery<MyQueryResult>(
    GRAPHQL_USER_ID_QUERY, { fetchPolicy: 'cache-first' }
  );
  const [subscribeMutation] = useMutation(GRAPHQL_SUBSCRIBE_MUTATION);
  const [unSubscribeMutation] = useMutation(GRAPHQL_UNSUBSCRIBE_MUTATION);

  if (isSubscribed) {
    return (
      <Button
        className={classname}
        modifier="black-light"
        onClick={() => {
          if (data && data.user) {
            showToast('toast.remove.subscribe');
            unSubscribeMutation(getSubscribeMutationOptions(props, false)).catch(
              () => showToast('toast.remove.subscribe.failed')
            );
            pushSubscribeAnalytics(name, -1, channelName);
          } else {
            const eventTrack: EventTrack = {
              'eventDetails.category': GA_EVENT_CATEGORY.DEFAULT,
              'eventDetails.action': 'intent to login',
              'eventDetails.label': 'subscribe'
            };
            openLoginPopup('unsubscribe', false, eventTrack);
          }
        }}>
        <FormattedMessage id="label.subscribed" />
      </Button>
    );
  } else {
    return (
      <Button
        className={classname}
        modifier="secondary"
        onClick={() => {
          if (data && data.user) {
            showToast('toast.add.subscribe');
            subscribeMutation(getSubscribeMutationOptions(props, true)).catch(
              () => showToast('toast.add.subscribe.failed')
            );
            pushSubscribeAnalytics(name, 1, channelName);
          } else {
            const eventTrack: EventTrack = {
              'eventDetails.category': GA_EVENT_CATEGORY.DEFAULT,
              'eventDetails.action': 'intent to login',
              'eventDetails.label': 'subscribe'
            };
            openLoginPopup('subscribe', false, eventTrack);
          }
        }}>
        <FormattedMessage id="label.subscribe" />
      </Button>
    );
  }
};
